import React, { FC, useEffect, useMemo } from "react";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { Button } from "@pd/pd-ui-kit";
import { Flex } from "antd";
import { Controller, useForm } from "react-hook-form/dist/index.ie11";
import { useTranslation } from "react-i18next";

import Preloader from "~/components/Preloader";
import { NOTIFICATIONS } from "~/constans/notifications";
import { useGetAllGroupsForSelect } from "~/hooks/Selectors/useGetAllGroupsForSelect";
import {
  useAddNotificationMutation,
  useSetGroupEducationEmployeeExpectationIndicatorMutation,
} from "~/schemas/generated/graphql";
import { Select } from "~/shared/ui/Select";

import s from "./SelectDealerForm.less";

interface IGroupEducationEmployeeExpectation {
  GroupTrainingId: string;
  GroupCertificationId: string;
  GroupTraining: {
    name: string;
  };
  GroupCertification: {
    name: string;
  };
}

interface IProps {
  handleCloseModal: () => void;
  groupEducationEmployeeExpectation: IGroupEducationEmployeeExpectation;
  editorStaffOne?;
  refetch?;
  refetchGroupEducationEmployeeExpectationState?;
  loading: boolean;
}

const NOT_SELECTED_VALUE = "";

const onPopupScrollBottom =
  (callback?: () => void) => (e: React.UIEvent<HTMLDivElement>) => {
    if (!callback) return;
    const target = e.currentTarget;
    if (target.scrollTop + target.clientHeight >= target.scrollHeight - 16) {
      callback();
    }
  };

const StaffSertificationForm: FC<IProps> = ({
  refetch,
  refetchGroupEducationEmployeeExpectationState,
  handleCloseModal,
  groupEducationEmployeeExpectation,
  loading,
}) => {
  const { t } = useTranslation();

  const schema = yup.object().shape({
    nameSertification: yup.string().required(t("validation.required")),
    nameObchenia: yup.string().required(t("validation.required")),
  });

  const { handleSubmit, control, errors, setValue, watch } = useForm({
    defaultValues: {
      nameSertification: NOT_SELECTED_VALUE,
      nameObchenia: NOT_SELECTED_VALUE,
    },
    resolver: yupResolver(schema),
  });

  const watchSertification = watch("nameSertification");
  const watchObchenia = watch("nameObchenia");

  const [addNotificationMutation] = useAddNotificationMutation({});
  const { groups, searchText, intersectionObserver } =
    useGetAllGroupsForSelect();

  const [mutationSertification, setMutationSetification] =
    useSetGroupEducationEmployeeExpectationIndicatorMutation({
      onCompleted(data) {
        handleCloseModal();
        refetch();
        refetchGroupEducationEmployeeExpectationState();
        // It's function breaks setValue functional, don't uncomment
        addNotificationMutation({
          variables: {
            type: NOTIFICATIONS.success,
            content: t("common.сhange_was_successful"),
          },
        });
      },
    });

  useEffect(() => {
    if (groupEducationEmployeeExpectation) {
      setValue(
        "nameSertification",
        groupEducationEmployeeExpectation?.GroupCertificationId ??
          NOT_SELECTED_VALUE,
      );
      setValue(
        "nameObchenia",
        groupEducationEmployeeExpectation?.GroupTrainingId ??
          NOT_SELECTED_VALUE,
      );
    }
  }, [groupEducationEmployeeExpectation, setValue]);

  // Выбранные группы подмешиваем в опции: справочник грузится постранично,
  // и без этого label предзаполненного значения может не найтись среди опций
  const groupOptions = useMemo(() => {
    const merged: { value: string; label: string }[] = [];
    const pushSelected = (id?: string, name?: string) => {
      if (id && !merged.some((o) => o.value === id)) {
        merged.push({ value: id, label: name ?? String(id) });
      }
    };
    pushSelected(
      groupEducationEmployeeExpectation?.GroupTrainingId,
      groupEducationEmployeeExpectation?.GroupTraining?.name,
    );
    pushSelected(
      groupEducationEmployeeExpectation?.GroupCertificationId,
      groupEducationEmployeeExpectation?.GroupCertification?.name,
    );
    groups.forEach((group) => {
      if (!merged.some((o) => o.value === group.value)) merged.push(group);
    });
    return [{ value: NOT_SELECTED_VALUE, label: "Не выбрано" }, ...merged];
  }, [groups, groupEducationEmployeeExpectation]);

  const onSubmit = (data) => {
    mutationSertification({
      variables: {
        GroupTrainingId: data?.nameObchenia || undefined,
        GroupCertificationId: data?.nameSertification || undefined,
      },
    });
  };

  if (loading) return <Preloader />;

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Flex vertical gap={16}>
        <Controller
          name="nameObchenia"
          control={control}
          render={({ value, onChange }) => (
            <div className={s.field}>
              <span className={s.fieldLabel}>
                {t("editorStaff.nameObchenia")}
              </span>
              <Select
                value={value ?? NOT_SELECTED_VALUE}
                onChange={(nextValue) => {
                  onChange(nextValue);
                  if (nextValue && nextValue === watchSertification) {
                    setValue("nameSertification", NOT_SELECTED_VALUE);
                  }
                }}
                options={groupOptions}
                showSearch
                filterOption={false}
                onSearch={searchText}
                onPopupScroll={onPopupScrollBottom(intersectionObserver)}
                status={errors.nameObchenia ? "error" : undefined}
              />
              {errors.nameObchenia && (
                <span className={s.errorText}>
                  {errors.nameObchenia?.message}
                </span>
              )}
            </div>
          )}
        />

        <Controller
          name="nameSertification"
          control={control}
          render={({ value, onChange }) => (
            <div className={s.field}>
              <span className={s.fieldLabel}>
                {t("editorStaff.nameSertification")}
              </span>
              <Select
                value={value ?? NOT_SELECTED_VALUE}
                onChange={(nextValue) => {
                  onChange(nextValue);
                  if (nextValue && nextValue === watchObchenia) {
                    setValue("nameObchenia", NOT_SELECTED_VALUE);
                  }
                }}
                options={groupOptions}
                showSearch
                filterOption={false}
                onSearch={searchText}
                onPopupScroll={onPopupScrollBottom(intersectionObserver)}
                status={errors.nameSertification ? "error" : undefined}
              />
              {errors.nameSertification && (
                <span className={s.errorText}>
                  {errors.nameSertification?.message}
                </span>
              )}
            </div>
          )}
        />

        <Flex justify="flex-end">
          <Button htmlType={"submit"} loading={setMutationSetification.loading}>
            {t("ui.save")}
          </Button>
        </Flex>
      </Flex>
    </form>
  );
};
export default StaffSertificationForm;
